/**
 * The local outbox.
 *
 * THE ONE INVARIANT THIS FILE EXISTS TO PROTECT
 * ---------------------------------------------
 * A business write and its outbox entry are written in the SAME Dexie
 * transaction. Never one and then the other.
 *
 * Write-then-enqueue: a crash between the two loses the change, and the record
 * silently diverges from every other device forever. There is no repair, because
 * nothing recorded that a repair was needed.
 *
 * Enqueue-then-write: a crash between the two publishes a change for a row that
 * does not exist, and other devices apply it — inventing a customer who was
 * never created.
 *
 * Neither window is a rare race. IndexedDB transactions are not free, a browser
 * can be killed mid-write, and a user closing a laptop lid is the single most
 * common way an app dies. So the pairing is enforced by the FUNCTION SIGNATURE
 * below: `enqueueChange` takes the Dexie `Transaction` it must join, and a
 * business service cannot call it without one.
 */
import Dexie, { type Table } from 'dexie';
import type { DNRDatabase } from '@data/db/database';
import type { OutboxStatus, SyncOutboxEntry } from '@data/db/types';
import type { SyncChange, SyncEntityType } from '@domain/sync/changeModel';
import type { DiskBackupTarget } from '@data/backup/diskBackupTarget';

export interface EnqueueInput {
  readonly businessId: string;
  readonly deviceId: string;
  readonly entityType: SyncEntityType;
  readonly entityId: string;
  readonly operation: 'UPSERT' | 'DELETE';
  readonly version: number;
  readonly changedAt: string;
  readonly payload?: Record<string, unknown>;
  readonly deletedAt?: string;
  /** The change id. Generated by the caller at the moment of the local write. */
  readonly changeId: string;
  readonly now?: string;
}

/**
 * Append one change to the outbox INSIDE an existing transaction.
 *
 * `table` MUST be the transaction-scoped outbox table — `tx.syncOutbox`, not
 * `db.syncOutbox` and not a freshly constructed `db.table('syncOutbox')`. A
 * table built outside the transaction's zone makes Dexie throw
 * `PrematureCommitError`, which is the correct failure: it means the write would
 * not have been part of the transaction, and an outbox entry written outside the
 * business write is exactly the split this function exists to prevent.
 */
export async function enqueueChange(
  table: Table<SyncOutboxEntry, string>,
  input: EnqueueInput,
): Promise<void> {
  await table.put(toOutboxEntry(input));
}

/**
 * Append SEVERAL changes in one `bulkPut`.
 *
 * Returns a `Dexie.Promise`, not a native one, and that is the point.
 *
 * Dexie decides a transaction may commit when it can no longer see an
 * outstanding promise OF ITS OWN. An `async` function — or any `.then()` off a
 * native promise — returns a promise Dexie does not track, so Dexie can conclude
 * the transaction is finished while this write is still in flight. The next write
 * then lands outside the transaction, or throws `PrematureCommitError`.
 *
 * The state that produces is the worst this system can be in: a stock movement
 * COMMITTED while the outbox is EMPTY. The goods have left the shop on this
 * device, no other device will ever hear about it, and the ledger is append-only
 * so nothing would correct it later.
 *
 * `table.bulkPut` is already a Dexie promise; it is returned directly rather than
 * awaited, which is what preserves the tracking.
 */
export function enqueueChanges(
  table: Table<SyncOutboxEntry, string>,
  inputs: readonly EnqueueInput[],
): Dexie.Promise<unknown> {
  if (inputs.length === 0) return Dexie.Promise.resolve(undefined);
  return table.bulkPut(inputs.map(toOutboxEntry)) as unknown as Dexie.Promise<unknown>;
}

function toOutboxEntry(input: EnqueueInput): SyncOutboxEntry {
  const now = input.now ?? new Date().toISOString();
  return {
    changeId: input.changeId,
    businessId: input.businessId,
    deviceId: input.deviceId,
    entityType: input.entityType,
    entityId: input.entityId,
    operation: input.operation,
    version: input.version,
    changedAt: input.changedAt,
    payload: input.payload,
    deletedAt: input.deletedAt,
    status: 'pending',
    retryCount: 0,
    createdAt: now,
  };
}

/**
 * Read a batch of pending changes for one business, oldest first.
 *
 * Oldest first, so a queue that cannot drain sends changes in the order they
 * happened. Sending newest-first would let a permanent failure at the head of
 * the queue be starved forever by new work arriving behind it.
 */
export async function takePending(
  db: DNRDatabase,
  businessId: string,
  limit: number,
): Promise<SyncOutboxEntry[]> {
  const all = await db.syncOutbox
    .where('[businessId+status]')
    .equals([businessId, 'pending'])
    .sortBy('createdAt');
  return all.slice(0, limit);
}

/** How many changes are waiting. This is the number the settings screen shows. */
export async function pendingCount(db: DNRDatabase, businessId: string): Promise<number> {
  return db.syncOutbox.where('[businessId+status]').equals([businessId, 'pending']).count();
}

/** How many changes permanently failed the provider. */
export async function failedCount(db: DNRDatabase, businessId: string): Promise<number> {
  return db.syncOutbox.where('[businessId+status]').equals([businessId, 'failed']).count();
}

/** The failed entries for a business, oldest first. */
export async function failedEntries(
  db: DNRDatabase,
  businessId: string,
): Promise<SyncOutboxEntry[]> {
  return db.syncOutbox
    .where('[businessId+status]')
    .equals([businessId, 'failed'])
    .sortBy('createdAt');
}

/**
 * Reset a failed entry back to `pending` so the next sync run can retry it.
 *
 * Only the status and retry counter are touched — the error history is preserved
 * so a second failure has the full picture. This is a manual user action, not an
 * automatic retry: non-retryable rejections (FORBIDDEN, REJECTED) will fail again,
 * but the user may have resolved the underlying issue (e.g. corrected the data,
 * fixed permissions, or waited out a transient server condition).
 */
export async function retryFailedEntry(db: DNRDatabase, changeId: string): Promise<void> {
  const entry = await db.syncOutbox.get(changeId);
  if (!entry || entry.status !== 'failed') return;
  await db.syncOutbox.update(changeId, {
    status: 'pending' as OutboxStatus,
    retryCount: 0,
  });
}

/**
 * Mark changes as accepted.
 *
 * Only called with ids the provider actually confirmed. A provider that accepts
 * a batch and then fails to record two of them must not be able to make us
 * believe those two are safe to discard.
 *
 * Takes the table rather than a `Transaction`: the acknowledgement is a state
 * update on the outbox alone, and it is written AFTER the business data has
 * already been committed by `applyChanges`. Wrapping it in its own transaction
 * would suggest a coupling that does not exist.
 */
export async function markAcked(
  table: Table<SyncOutboxEntry, string>,
  changeIds: readonly string[],
): Promise<void> {
  if (changeIds.length === 0) return;
  await table.bulkUpdate(
    changeIds.map((id) => ({ key: id, changes: { status: 'acked' as OutboxStatus } })),
  );
}

/**
 * Record a failed attempt.
 *
 * The `retryable` flag matters more than the counter. A mutation the provider
 * permanently refuses must move to `failed`, not sit in `pending` retrying
 * forever — otherwise the pending count is a permanent lie and the user has no
 * way to discover which change is stuck.
 */
export async function markAttemptFailed(
  db: DNRDatabase,
  changeId: string,
  message: string,
  retryable: boolean,
  now = new Date().toISOString(),
): Promise<void> {
  const entry = await db.syncOutbox.get(changeId);
  if (!entry) return;
  await db.syncOutbox.put({
    ...entry,
    status: retryable ? 'pending' : 'failed',
    retryCount: entry.retryCount + 1,
    lastAttemptAt: now,
    lastError: message,
    // Recorded so a retry counter can never be mistaken for a verdict. The
    // counter counts ATTEMPTS, and a network outage increments it exactly like
    // a rejection does; only this flag says the provider refused the change.
    lastAttemptRetryable: retryable,
  });
}

/**
 * Delete acknowledged entries older than a cutoff.
 *
 * This is the LOCAL half of compaction. The remote half is `oldestNeededCursor`
 * on the provider. Both must agree before a change is gone from both sides — a
 * change dropped locally while a remote device still needs it is survivable
 * (it is re-pulled), whereas the reverse is data loss.
 */
export async function pruneAcked(
  db: DNRDatabase,
  businessId: string,
  before: string,
): Promise<number> {
  const acked = await db.syncOutbox
    .where('[businessId+status]')
    .equals([businessId, 'acked'])
    .filter((e) => e.createdAt < before)
    .primaryKeys();
  await db.syncOutbox.bulkDelete(acked);
  return acked.length;
}

/** A cursor into the local outbox, derived from the durable disk manifest.
 *
 * The disk scheduler advances this point only after the journal append
 * succeeds and the manifest is rewritten. Anything strictly before it is
 * therefore already on the backup disk, underneath an independent copy.
 * Retention code (local compaction, and anything that gates cloud pruning)
 * consults this cursor so it never deletes a row the disk has not verified.
 */
export interface DiskCheckpointCursor {
  readonly at: string;
  readonly id: string;
}

/**
 * Read the durable disk checkpoint cursor from `manifest.json`, or null when no
 * manifest has ever been written. Returns null instead of throwing, so a status
 * poll can never surface an unhandled rejection (mirrors the engine's policy).
 */
export async function readDiskCheckpointCursor(
  target: DiskBackupTarget,
): Promise<DiskCheckpointCursor | null> {
  try {
    const manifestText = await target.readFile('manifest.json');
    if (manifestText === null) return null;
    const parsed: Record<string, unknown> = JSON.parse(manifestText);
    return {
      at: typeof parsed.lastJournaledAt === 'string' ? parsed.lastJournaledAt : '',
      id: typeof parsed.lastJournaledChangeId === 'string' ? parsed.lastJournaledChangeId : '',
    };
  } catch {
    return null;
  }
}

/**
 * True when at least one PENDING outbox entry is strictly before the cursor.
 *
 * The outbox is ordered by `createdAt` with `changeId` breaking ties, which is
 * exactly the order `runDiskBackup` drains, so a `true` here means the disk has
 * not yet reached this point in the change log and must keep the change.
 */
export async function hasPendingOutboxBefore(
  db: DNRDatabase,
  businessId: string,
  at: string,
  id: string,
): Promise<boolean> {
  if (!at && !id) return false;
  const all = await db.syncOutbox.toArray();
  for (const entry of all) {
    if (entry.businessId !== businessId) continue;
    if (entry.status !== 'pending') continue;
    if (entry.createdAt !== at) {
      if (entry.createdAt < at) return true;
    } else if (entry.changeId < id) {
      return true;
    }
  }
  return false;
}

/**
 * True only when it is safe to delete acknowledged outbox entries before a
 * cursor: no pending outbox entry is still older than that cursor.
 *
 * A pending entry older than the cutoff means the change has not yet been
 * journaled to disk, so deleting the acked outbox row would leave the disk
 * copy as the only copy. If the disk scheduler has already caught up, this is
 * true for every cutoff at or after the manifest's `lastJournaledAt`/id.
 */
export async function canPruneOutboxBefore(
  db: DNRDatabase,
  businessId: string,
  at: string,
  id: string,
): Promise<boolean> {
  return !(await hasPendingOutboxBefore(db, businessId, at, id));
}

/** Re-queue permanently failed changes, e.g. after the user fixes the cause. */
export async function retryFailed(db: DNRDatabase, businessId: string): Promise<number> {
  const failed = await db.syncOutbox
    .where('[businessId+status]')
    .equals([businessId, 'failed'])
    .primaryKeys();
  await db.syncOutbox.bulkUpdate(
    failed.map((id) => ({
      key: id,
      // The counter is reset with the status, and that is not cosmetic. A
      // manual retry that left it at the ceiling was refused by the engine's
      // attempt guard before the provider was called at all: the one action
      // offered to the user did nothing, every time, and the error said only
      // "gave up after repeated failures".
      changes: { status: 'pending' as OutboxStatus, retryCount: 0 },
    })),
  );
  return failed.length;
}

/** Convert an outbox entry into the wire shape. */
export function toWireChange(entry: SyncOutboxEntry): SyncChange {
  return {
    changeId: entry.changeId,
    businessId: entry.businessId,
    entityType: entry.entityType as SyncEntityType,
    entityId: entry.entityId,
    operation: entry.operation,
    version: entry.version,
    deviceId: entry.deviceId,
    changedAt: entry.changedAt,
    payload: entry.payload,
    deletedAt: entry.deletedAt,
  };
}
